/**
 * Veroni bag popup — the add-to-cart confirmation.
 *
 * Why this exists: before 2026-08-07 the store gave a shopper NO visible
 * feedback when they added a piece. `settings.auto_open_cart_drawer` is off,
 * so the only signal was the header bubble incrementing by one. Shoppers
 * clicked Add to cart twice, or assumed it had failed.
 *
 * This is deliberately NOT the full cart drawer: it is a small, dismissible
 * bag card in the upper-right corner showing the piece that was just added,
 * plus the bundle nudge. The drawer still opens from the header bag icon.
 *
 * Wiring: Horizon dispatches Shopify's standard `shopify:cart:lines-update`
 * event on add. The event carries `lines[].merchandiseId` up front and an
 * `event.promise` that resolves once the server round-trip is done with
 * `{ cart, detail: { items, itemCount, didError } }` where `detail.items` is
 * the AJAX cart's item list. We identify the line that actually changed by
 * diffing line keys/quantities against the previous cart snapshot — matching
 * on variant id alone is not enough, because the same variant can occupy two
 * separate lines when line-item properties differ (e.g. two ring sizes).
 */

import { formatMoney } from '@theme/money-formatting';

/** How long the popup stays up before auto-dismissing, in ms. */
const DISMISS_AFTER = 7000;

/** Grace period so a burst of adds does not thrash the card. */
const RESHOW_DEBOUNCE = 120;

class VeroniBagPopup extends HTMLElement {
  /** @type {Map<string, number>} line key -> quantity, as of the last settled cart */
  #previous = new Map();

  /** @type {boolean} */
  #seeded = false;

  /** @type {number | undefined} */
  #dismissTimer;

  /** @type {number | undefined} */
  #showTimer;

  /**
   * Re-created per connection so a disconnect/reconnect (view transitions,
   * section morphs) cannot leave every listener bound to an already-aborted
   * signal.
   * @type {AbortController}
   */
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('shopify:cart:lines-update', this.#onCartLinesUpdate, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });

    this.addEventListener('pointerenter', this.#holdOpen, { signal });
    this.addEventListener('pointerleave', this.#resumeDismiss, { signal });
    this.addEventListener('focusin', this.#holdOpen, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    this.querySelector('[data-bag-close]')?.addEventListener('click', () => this.hide(), { signal });
    this.querySelector('[data-bag-view]')?.addEventListener('click', this.#openDrawer, { signal });

    // The cart drawer opens non-modally, so it does not get the top layer and
    // this card would sit on top of it. Two views of the same cart, one
    // covering the other, is just clutter — yield to the drawer.
    const drawer = document.querySelector('#cart-drawer');
    if (drawer) {
      new MutationObserver(() => {
        if (drawer.hasAttribute('open')) this.hide();
      }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }

    // Seed the "before" snapshot off the critical path so the first add can be
    // diffed. If an add lands before this resolves we fall back to matching on
    // merchandise id, which is correct for every cart except the multi-line
    // same-variant case.
    const seed = () => {
      fetch(`${Theme?.routes?.cart_url ?? '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => cart && this.#snapshot(cart.items))
        .catch(() => {
          /* a failed seed only costs us the diff, not the popup */
        });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(seed, { timeout: 3000 });
    else setTimeout(seed, 1200);
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#dismissTimer);
    clearTimeout(this.#showTimer);
  }

  /** @param {Array<{key: string, quantity: number}>} items */
  #snapshot(items) {
    this.#previous = new Map((items ?? []).map((i) => [i.key, i.quantity]));
    this.#seeded = true;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('open')) this.hide();
  };

  #holdOpen = () => clearTimeout(this.#dismissTimer);

  #resumeDismiss = () => {
    if (this.hasAttribute('open')) this.#armDismiss();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (!next || !this.contains(next)) this.#resumeDismiss();
  };

  #armDismiss() {
    clearTimeout(this.#dismissTimer);
    this.#dismissTimer = setTimeout(() => this.hide(), DISMISS_AFTER);
  }

  #openDrawer = (/** @type {MouseEvent} */ event) => {
    event.preventDefault();
    this.hide();
    const drawer = /** @type {HTMLElement & { open?: () => void }} */ (document.querySelector('#cart-drawer'));
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    // No drawer on this page (cart template renders none) — fall through to /cart.
    window.location.href = Theme?.routes?.cart_url ?? '/cart';
  };

  /** @param {any} event Shopify CartLinesUpdateEvent */
  #onCartLinesUpdate = (event) => {
    if (event?.action !== 'add') return;

    const requested = (event.lines ?? [])
      .map((/** @type {{merchandiseId?: string}} */ line) => Number(String(line?.merchandiseId ?? '').split('/').pop()))
      .filter(Boolean);

    Promise.resolve(event.promise)
      .then((result) => {
        const items = result?.detail?.items;
        if (!Array.isArray(items)) return;
        if (result?.detail?.didError) {
          this.#snapshot(items);
          return;
        }

        const added = this.#findAddedLine(items, requested);
        this.#snapshot(items);
        if (added) this.#render(added, items);
      })
      .catch(() => {
        /* the add failed; product-form already surfaces the error */
      });
  };

  /**
   * @param {Array<any>} items post-add cart lines
   * @param {number[]} requested variant ids from the add event
   * @returns {any | null}
   */
  #findAddedLine(items, requested) {
    if (this.#seeded) {
      // A brand-new line beats a quantity bump: adding a second ring size
      // creates a new key while the old line is untouched.
      const fresh = items.filter((i) => !this.#previous.has(i.key));
      if (fresh.length) return this.#preferRequested(fresh, requested);

      const bumped = items.filter((i) => i.quantity > (this.#previous.get(i.key) ?? 0));
      if (bumped.length) return this.#preferRequested(bumped, requested);
    }

    const byVariant = items.filter((i) => requested.includes(i.variant_id));
    // Last match wins: Shopify appends new lines, so the newest same-variant
    // line is the one the shopper just created.
    if (byVariant.length) return byVariant[byVariant.length - 1];

    return items[items.length - 1] ?? null;
  }

  /**
   * @param {Array<any>} candidates
   * @param {number[]} requested
   */
  #preferRequested(candidates, requested) {
    return candidates.find((i) => requested.includes(i.variant_id)) ?? candidates[0];
  }

  /**
   * @param {any} line the cart line that was just added
   * @param {Array<any>} items every cart line, post-add
   */
  #render(line, items) {
    const refs = {
      image: /** @type {HTMLImageElement | null} */ (this.querySelector('[data-bag-image]')),
      placeholder: this.querySelector('[data-bag-placeholder]'),
      title: this.querySelector('[data-bag-title]'),
      meta: this.querySelector('[data-bag-meta]'),
      price: this.querySelector('[data-bag-price]'),
      status: this.querySelector('[data-bag-status]'),
      bundle: this.querySelector('[data-bag-bundle]'),
    };

    const title = line.product_title || line.title || '';
    if (refs.title) refs.title.textContent = title;

    // Variant options plus any line-item properties the shopper chose (ring
    // size lives in properties, not in the variant).
    const options = (line.options_with_values ?? [])
      .filter((/** @type {any} */ o) => o?.value && o.value !== 'Default Title')
      .map((/** @type {any} */ o) => o.value);
    const properties = Object.entries(line.properties ?? {})
      .filter(([key, value]) => value && !key.startsWith('_'))
      .map(([key, value]) => `${key} ${value}`);
    if (refs.meta) refs.meta.textContent = [...options, ...properties].join(' · ');

    if (refs.price) refs.price.textContent = this.#money(line.final_line_price ?? line.line_price ?? line.price);

    const src = line.featured_image?.url || line.image;
    if (refs.image && src) {
      // Cart images come back at full size; ask the CDN for a 2x thumbnail.
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '240');
      refs.image.src = url.toString();
      refs.image.alt = line.featured_image?.alt || title;
      refs.image.hidden = false;
      refs.placeholder?.setAttribute('hidden', '');
    } else if (refs.image) {
      refs.image.hidden = true;
      refs.image.removeAttribute('src');
      refs.placeholder?.removeAttribute('hidden');
    }

    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    if (refs.status) {
      refs.status.textContent =
        count === 1 ? 'Added to your bag — 1 piece' : `Added to your bag — ${count} pieces`;
    }

    this.#renderBundle(refs.bundle, count);
    this.show();
  }

  /**
   * The bundle nudge: 10% off the whole order once the bag holds two or more
   * pieces. Any saving printed here is read back off the real cart, never
   * computed locally, so the card can never promise what checkout won't give.
   * @param {Element | null} node
   * @param {number} count total quantity in the bag
   */
  #renderBundle(node, count) {
    if (!(node instanceof HTMLElement)) return;
    if (this.dataset.bundleEnabled !== 'true') {
      node.hidden = true;
      return;
    }

    const percent = this.dataset.bundlePercent || '10';
    node.hidden = false;

    if (count < 2) {
      node.textContent = `Add one more piece and take ${percent}% off your whole order.`;
      return;
    }

    node.textContent = `${percent}% bundle discount applies at checkout.`;

    fetch(`${Theme?.routes?.cart_url ?? '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || !node.isConnected) return;
        const saved = Number(cart.total_discount) || 0;
        if (saved > 0) {
          node.textContent = `${percent}% bundle discount applied — you saved ${this.#money(saved)}.`;
        }
      })
      .catch(() => {
        /* keep the forward-looking line */
      });
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    try {
      return formatMoney(Number(cents) || 0, format, currency);
    } catch {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  show() {
    // Visibility is CSS-only ([open] on the host) so the card can transition in
    // and out; never toggle `hidden`, which would kill the transition.
    clearTimeout(this.#showTimer);
    this.#showTimer = setTimeout(() => {
      this.setAttribute('open', '');
      this.#armDismiss();
    }, RESHOW_DEBOUNCE);
  }

  hide() {
    clearTimeout(this.#dismissTimer);
    this.removeAttribute('open');
  }
}

if (!customElements.get('veroni-bag-popup')) {
  customElements.define('veroni-bag-popup', VeroniBagPopup);
}
